import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase, checkSupabaseConnection } from '../lib/supabase';

export type UserRole = 'tenant' | 'landlord' | 'management';

export interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  phone: string | null;
  created_at: string;
}

export interface AuthUser {
  id: string;
  email: string;
}

interface AuthContextType {
  user: AuthUser | null;
  profile: Profile | null;
  role: UserRole | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSupabaseReady: boolean;
  profileLoading: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isJwtFutureError(err: any): boolean {
  return (
    err?.code === 'PGRST303' ||
    err?.message?.includes('JWT issued at future') ||
    err?.message?.includes('JWT expired') ||
    err?.code === 'PGRST301'
  );
}

function isRefreshTokenError(err: any): boolean {
  return (
    err?.message?.includes('Refresh Token Not Found') ||
    err?.message?.includes('Invalid Refresh Token') ||
    err?.code === 'refresh_token_not_found'
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isSupabaseReady, setIsSupabaseReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const fetchProfile = async (userId: string, attempt = 1): Promise<void> => {
    setProfileLoading(true);
    setAuthError(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        if (isJwtFutureError(error) && attempt === 1) {
          console.warn('[Auth] JWT clock skew detected, refreshing session...');
          const { error: refreshErr } = await supabase.auth.refreshSession();
          if (refreshErr) {
            if (isRefreshTokenError(refreshErr)) {
              console.warn('[Auth] Refresh token invalid during profile fetch, signing out...');
              await supabase.auth.signOut({ scope: 'local' });
              setUser(null);
              setProfile(null);
              setAuthError(null);
              return;
            }
            console.error('[Auth] Session refresh failed:', refreshErr);
          }
          if (!refreshErr) {
            return fetchProfile(userId, 2);
          }
        }
        console.error('Error fetching profile:', error);
        setProfile(null);
        if (isJwtFutureError(error)) {
          setAuthError('Your session appears out of sync. Please sign in again.');
          await supabase.auth.signOut();
          setUser(null);
        }
      } else if (data) {
        setProfile(data as Profile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error('Profile fetch failed:', err);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const connected = await checkSupabaseConnection();
        if (mounted) setIsSupabaseReady(connected);

        if (!connected) {
          if (mounted) setIsLoading(false);
          return;
        }

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          if (isJwtFutureError(sessionError)) {
            console.warn('[Auth] Session JWT skew on init, clearing...');
            await supabase.auth.signOut();
            if (mounted) {
              setIsLoading(false);
              setAuthError('Session expired. Please sign in again.');
            }
            return;
          }
          if (isRefreshTokenError(sessionError)) {
            console.warn('[Auth] Refresh token invalid on init, clearing session...');
            // Sign out locally to clear the bad token from storage
            await supabase.auth.signOut({ scope: 'local' });
            if (mounted) {
              setIsLoading(false);
              setAuthError(null); // Don't show error — user just needs to re-login normally
            }
            return;
          }
          console.error('[Auth] getSession error:', sessionError);
        }

        if (!mounted) return;
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
          });
          await fetchProfile(session.user.id);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    initAuth();

    let unsub: (() => void) | undefined;
    try {
      // IMPORTANT: The onAuthStateChange callback MUST NOT be async/await.
      // It runs inside Supabase SDK's internal lock context — awaiting any
      // supabase call from inside the callback causes a deadlock on page refresh.
      // Use .then() for any non-blocking data fetching.
      const result = supabase.auth.onAuthStateChange((event, session) => {
        if (!mounted) return;
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setAuthError(null);
        } else if (event === 'TOKEN_REFRESHED') {
          setAuthError(null);
          if (session?.user) {
            setUser({
              id: session.user.id,
              email: session.user.email || '',
            });
          }
        } else if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
          });
          // Use .then() instead of await to avoid deadlock inside SDK's lock context
          fetchProfile(session.user.id);
        }
      });

      const subscription = (result as any).data?.subscription || (result as any).subscription || (result as any);
      if (typeof subscription?.unsubscribe === 'function') {
        unsub = () => subscription.unsubscribe();
      }
    } catch (err) {
      console.error('Auth listener setup failed:', err);
    }

    return () => {
      mounted = false;
      unsub?.();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setAuthError(null);
      const connected = await checkSupabaseConnection();
      if (!connected) {
        return { error: new Error('Unable to connect to the authentication server. Please check your internet connection and try again.') };
      }
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        return { error };
      }
      if (data.user) {
        setUser({
          id: data.user.id,
          email: data.user.email || '',
        });
        await fetchProfile(data.user.id);
      }
      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign in failed';
      return { error: new Error(msg) };
    }
  };

  // Public registration: ALWAYS creates a tenant account.
  // The role is hardcoded server-side — the frontend can never influence it.
  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      setAuthError(null);
      const connected = await checkSupabaseConnection();
      if (!connected) {
        return { error: new Error('Unable to connect to the authentication server. Please check your internet connection and try again.') };
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'tenant',
          },
        },
      });

      if (error) {
        console.error('Raw signUp error:', error);
        return { error: new Error(`Registration failed: ${error.message}`) };
      }

      if (!data.user) {
        return { error: new Error('Account created but user data was not returned. Please try logging in.') };
      }

      // The auth.users trigger is now a no-op, so we MUST create the profile row
      // immediately and treat failure as a hard error.
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: data.user.id,
            email: data.user.email || email,
            full_name: fullName || email,
            role: 'tenant',
          },
          { onConflict: 'id' }
        );

      if (profileError) {
        console.error('Profile insert error after signUp:', profileError);
        return {
          error: new Error(
            'Your account was created, but we could not set up your profile. ' +
            'Please contact support or try logging in.'
          ),
        };
      }

      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign up failed';
      return { error: new Error(msg) };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
    setUser(null);
    setProfile(null);
    setAuthError(null);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const clearAuthError = () => setAuthError(null);

  const value: AuthContextType = {
    user,
    profile,
    role: profile?.role || null,
    isLoading,
    isAuthenticated: !!user,
    isSupabaseReady,
    profileLoading,
    authError,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    clearAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}